import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { ListFilter, Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import PaginatorCst from "@components/PaginatorCst";
import InputCst from "@components/InputCst/InputCst";
import FiltersAccordionCst from "@components/FiltersAccordionCst";
import { PagesID } from "@utils/utilsConfigurations";
import { Search as SearchIcon } from "lucide-react";
import { Media } from "src/interfaces/Media";
import { resolveUploadAccept } from "@utils/mediaTypeAccept";
import { useAddEditLibrary } from "@hooks/useAddEditLibrary";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useMediaList } from "./hooks/useMediaList";
import MediaPickerCard from "./components/MediaPickerCard";
import { MediaLibraryPickerProps, MediaPickerItem } from "./types";

const PAGE_SIZE = 16;
// Ricerca nascosta: si usano i filtri canonici (accordion). Rimettere true per riattivarla.
const MEDIA_SEARCH_ENABLED = false;

const toItem = (m: Media): MediaPickerItem => ({
  id: m.id,
  label: m.metadata?.name || `Media #${m.id}`,
});

const MediaLibraryPickerCst: React.FC<MediaLibraryPickerProps> = (props) => {
  const {
    open,
    onClose,
    title,
    mediaType,
    learningResourceType,
    autoOpenUpload,
  } = props;
  const { t } = useTranslation("media-library-picker");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [searchSubmitted, setSearchSubmitted] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});

  const { items, totalItems, totalPages, isLoading, mutate } = useMediaList({
    corporateId,
    mediaType,
    learningResourceType,
    page,
    pageSize,
    search: searchSubmitted || undefined,
    fieldFilters,
  });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const acceptString = mediaType
    ? resolveUploadAccept(mediaType, learningResourceType)
    : "";
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const handleStatus = useCallback(
    (args: { s: StatusEnum; message: string }) => {
      if (args.s === StatusEnum.LOADING) {
        setIsUploading(true);
        setUploadError(null);
      } else if (args.s === StatusEnum.OK) {
        setIsUploading(false);
        setPage(1);
        mutate();
      } else if (args.s === StatusEnum.KO) {
        setIsUploading(false);
        setUploadError(args.message || "Upload fallito");
      }
    },
    [mutate],
  );
  const { onSubmit: submitUpload } = useAddEditLibrary(
    corporateId || "1",
    undefined,
    handleStatus,
  );

  const [selected, setSelected] = useState<MediaPickerItem[]>([]);

  useEffect(() => {
    if (!open) return;
    if (props.multiple) {
      setSelected(props.initialSelection ?? []);
    } else {
      setSelected(props.initialSelection ? [props.initialSelection] : []);
    }
    setPage(1);
    setSearchInput("");
    setSearchSubmitted("");
  }, [open, props.multiple, props.initialSelection]);

  useEffect(() => {
    if (open && autoOpenUpload && mediaType) {
      const handle = setTimeout(() => {
        fileInputRef.current?.click();
      }, 50);
      return () => clearTimeout(handle);
    }
  }, [open, autoOpenUpload, mediaType]);

  const selectedSet = useMemo(
    () => new Set(selected.map((s) => s.id)),
    [selected],
  );

  const handleCardClick = (item: Media) => {
    if (props.multiple) {
      setSelected((prev) => {
        const isSelected = prev.some((s) => s.id === item.id);
        if (isSelected) return prev.filter((s) => s.id !== item.id);
        return [...prev, toItem(item)];
      });
    } else {
      setSelected((prev) => {
        const isSelected = prev[0]?.id === item.id;
        return isSelected ? [] : [toItem(item)];
      });
    }
  };

  const handleSubmitSearch = () => {
    setSearchSubmitted(searchInput.trim());
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitSearch();
    }
  };

  const handleConfirm = () => {
    if (props.multiple) {
      props.onConfirm(selected);
    } else {
      props.onConfirm(selected[0] ?? null);
    }
    onClose();
  };

  const handleUploadNew = () => {
    if (isUploading || !mediaType) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    e.target.value = "";
    if (!file || !mediaType) return;

    const inputForm = {
      name: file.name.replace(/\.[^/.]+$/, ""),
      mediaType,
      learningResourceType,
    };

    await submitUpload(inputForm, { file });
  };

  const canConfirm = selected.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "12px 24px 16px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <Button
            onClick={() => setFiltersOpen((prev) => !prev)}
            variant="outlined"
            startIcon={<ListFilter size={16} />}
            sx={{
              height: "40px",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              flexShrink: 0,
              borderColor: filtersOpen
                ? theme.palette.primary.main
                : theme.palette.divider,
              color: filtersOpen
                ? theme.palette.primary.main
                : theme.palette.text.primary,
              backgroundColor: filtersOpen
                ? theme.palette.action.hover
                : "transparent",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            {t("filters-button") || "Filtri"}
          </Button>

          {MEDIA_SEARCH_ENABLED && (
            <>
              <Box
                sx={{
                  flex: 1,
                  "& .MuiFormControl-root": {
                    minWidth: "0 !important",
                    width: "100% !important",
                  },
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  "& .MuiFormHelperText-root": { display: "none" },
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
                }}
                onKeyDown={handleSearchKeyDown}
              >
                <InputCst
                  id="media-library-search"
                  label={t("search-placeholder") || ""}
                  value={searchInput}
                  onChange={(e: any) => setSearchInput(e.target.value)}
                  endAdornment={
                    <SearchIcon size={16} color={theme.palette.text.tertiary} />
                  }
                />
              </Box>
              <Button
                onClick={handleSubmitSearch}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "8px",
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  flexShrink: 0,
                  "&:hover": {
                    borderColor: theme.palette.text.primary,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                {t("search-button")}
              </Button>
            </>
          )}

          <Box sx={{ flex: 1 }} />

          <Button
            onClick={handleUploadNew}
            variant="text"
            disabled={isUploading || !mediaType}
            startIcon={
              isUploading ? (
                <CircularProgress size={14} />
              ) : (
                <Upload size={16} />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            {isUploading ? t("uploading") : t("upload-new")}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptString}
            onChange={handleFileSelected}
            style={{ display: "none" }}
          />
        </Box>

        <Box sx={{ mb: filtersOpen ? 2.5 : 0 }}>
          <FiltersAccordionCst
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            pageId={PagesID["corporate.library.filters"]}
            value={fieldFilters}
            onApply={(next) => {
              setFieldFilters(next);
              setPage(1);
            }}
          />
        </Box>

        {uploadError && (
          <Box
            sx={{
              mb: 2,
              padding: "10px 14px",
              borderRadius: "8px",
              border: `1px solid ${theme.palette.error.main}`,
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.error.main,
              fontSize: "13px",
            }}
          >
            {uploadError}
          </Box>
        )}

        {/* Grid 4 colonne (responsive) */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 6,
              color: theme.palette.text.tertiary,
              fontSize: "13px",
            }}
          >
            {t("empty")}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {items.map((item) => (
              <Grid key={item.id} item xs={12} sm={6} md={4} lg={3}>
                <MediaPickerCard
                  item={item}
                  selected={selectedSet.has(item.id)}
                  onClick={() => handleCardClick(item)}
                  corporateId={corporateId || "1"}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <PaginatorCst
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px 20px", gap: 2 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!canConfirm}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          }}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MediaLibraryPickerCst;
